import React, { useEffect, useState } from "react";
import { resolveMediaUrl } from "./cache";
import { IPlayerLayout, IPlayerMedia, IPlayerZone, IPlayerZoneMedia } from "./types";
import WebZoneView from "../../Components/WebZone/WebZoneView";
import { readWebsiteConfig } from "../Template/Edit/websiteConfig";
import MenuBoard, { MenuBoardPayload } from "../../Components/MenuBoard/MenuBoard";
import { fetchCurrentWeather, weatherEmoji, WEATHER_TTL_MS, CurrentWeather } from "./weather";

/**
 * V2 — full-bleed CSS-absolute layout renderer.
 *
 * Renders a Template's zones as percentage-positioned `<div>`s on top
 * of a coloured / image background. Widget bodies are concrete for
 * the most common types (image / video / text / clock); the niche
 * widgets (weather / qr / menu_board) render readable placeholder
 * blocks that show the configured props so the operator can confirm
 * the layout is wired correctly. Real widget runtimes can be slotted
 * in here without changing the surrounding plumbing.
 *
 * Coordinate model:
 *   - x / y / w / h are stored on the server as 0..100 percentages of
 *     the canvas. We map them straight to `%` on the absolute zones.
 *   - The container itself is 100% of its parent (the player viewport)
 *     so the layout fills the screen, identical to the V1 media path.
 */
const LayoutRenderer: React.FC<{ layout: IPlayerLayout }> = ({ layout }) => {
  const sorted = [...(layout.zones || [])].sort(
    (a, b) => (a.z_order ?? 0) - (b.z_order ?? 0),
  );
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: layout.background || "#000",
        overflow: "hidden",
      }}
    >
      {sorted.map((z) => (
        <div
          key={z.id}
          style={{
            position: "absolute",
            left: `${z.x}%`,
            top: `${z.y}%`,
            width: `${z.w}%`,
            height: `${z.h}%`,
            zIndex: (z.z_order ?? 0) + 1,
            overflow: "hidden",
          }}
        >
          <ZoneWidget zone={z} />
        </div>
      ))}
    </div>
  );
};

/**
 * The single source of truth for rendering ONE zone's widget. Used by both
 * device playback (LayoutRenderer above) and the admin Template Preview, so the
 * preview looks exactly like the TV — there is no separate placeholder renderer.
 * `media` for image/video is resolved server-side into widget_config (identical
 * shape for the device layout and the /templates/{id} payload).
 */
export const ZoneWidget: React.FC<{ zone: IPlayerZone }> = ({ zone }) => {
  switch (zone.widget_type) {
    case "image":      return <ImageWidget cfg={zone.widget_config as any} />;
    case "video":      return <VideoWidget cfg={zone.widget_config as any} />;
    case "website":
    case "iframe_video":
    case "iframe_map":
    case "iframe_embed": return <WebsiteWidget cfg={zone.widget_config as any} />;
    case "text":       return <TextWidget cfg={zone.widget_config as any} />;
    case "clock":      return <ClockWidget cfg={zone.widget_config as any} />;
    case "qr":         return <QrWidget cfg={zone.widget_config as any} />;
    case "weather":    return <WeatherWidget cfg={zone.widget_config as any} />;
    case "menu_board": return <MenuBoardWidget cfg={zone.widget_config as any} />;
    default:           return <PlaceholderWidget label={String(zone.widget_type)} cfg={zone.widget_config as any} />;
  }
};

/* ── Concrete widgets ─────────────────────────────────────────────── */

/**
 * Convert the server's resolved-media block into the IPlayerMedia
 * shape `resolveMediaUrl()` expects. Returning null means there's
 * nothing to render (designer hasn't picked a media yet, or the
 * media row was filtered out by the cross-tenant check on the server).
 */
function toPlayerMedia(cfg: { media?: IPlayerZoneMedia | null } | null | undefined): IPlayerMedia | null {
  const m = cfg?.media;
  if (!m || !m.url) return null;
  return {
    id: m.id,
    type: m.type,
    url: m.url,
    duration: m.duration,
    sort_order: 0,
    checksum: m.checksum,
    file_size: m.file_size,
  };
}

const ImageWidget: React.FC<{ cfg: { media?: IPlayerZoneMedia | null; fit?: "cover" | "contain" | "fill" } }> = ({ cfg }) => {
  const media = toPlayerMedia(cfg);
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!media) { setSrc(null); return; }
    let cancelled = false;
    void (async () => {
      const url = await resolveMediaUrl(media);
      if (!cancelled) setSrc(url);
    })();
    return () => { cancelled = true; };
  }, [media?.checksum, media?.url]);

  if (!media) return <PlaceholderWidget label="Image" cfg={cfg} />;
  if (!src)   return <Box />; // brief blank while the blob URL resolves

  return (
    <img
      src={src}
      alt=""
      style={{
        width: "100%", height: "100%",
        objectFit: (cfg?.fit ?? "cover") as React.CSSProperties["objectFit"],
        display: "block",
      }}
    />
  );
};

/** A page URL for a hosted video service (not a directly-playable file). */
function isVideoPageUrl(url: string): boolean {
  return /(?:youtube\.com|youtu\.be|vimeo\.com|dailymotion\.com)/i.test(url);
}
/** Only http(s) sources are ever loaded (guards against javascript:/data: URLs). */
function safeHttpUrl(url: string | undefined | null): string | null {
  const u = (url ?? "").trim();
  return /^https?:\/\//i.test(u) ? u : null;
}

const VideoWidget: React.FC<{
  cfg: {
    media?: IPlayerZoneMedia | null; loop?: boolean; muted?: boolean;
    fit?: "cover" | "contain" | "fill"; source?: string; video_url?: string;
  };
}> = ({ cfg }) => {
  // Two sources: a Media-Library asset (resolved via the offline cache) or a
  // direct video URL. URL wins only when explicitly selected so existing
  // library-backed zones are untouched.
  const useUrl = cfg?.source === "url";
  const urlSrc = useUrl ? safeHttpUrl(cfg?.video_url) : null;
  const media = useUrl ? null : toPlayerMedia(cfg);

  const [libSrc, setLibSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!media) { setLibSrc(null); return; }
    let cancelled = false;
    void (async () => {
      const url = await resolveMediaUrl(media);
      if (!cancelled) setLibSrc(url);
    })();
    return () => { cancelled = true; };
  }, [media?.checksum, media?.url]);

  // URL source: reject YouTube/Vimeo *pages* — they are not <video> sources.
  if (useUrl) {
    if (!urlSrc) return <PlaceholderWidget label="Video" cfg={{ note: "Enter a video URL" }} />;
    if (isVideoPageUrl(urlSrc)) {
      return <PlaceholderWidget label="Video" cfg={{ note: "Use a direct .mp4/.webm URL — YouTube/Vimeo pages aren't supported" }} />;
    }
  } else {
    if (!media) return <PlaceholderWidget label="Video" cfg={{ note: "No video selected" }} />;
    if (!libSrc) return <Box />;
  }

  const src = (useUrl ? urlSrc : libSrc) as string;
  return (
    <video
      src={src}
      autoPlay
      muted={cfg?.muted !== false}
      loop={cfg?.loop !== false}
      playsInline
      style={{ width: "100%", height: "100%", objectFit: (cfg?.fit ?? "cover") as React.CSSProperties["objectFit"], display: "block" }}
      {...({ "webkit-playsinline": "true" } as Record<string, string>)}
    />
  );
};

const Box: React.FC<{}> = () => <div style={{ width: "100%", height: "100%" }} />;

/**
 * menu_board device widget. Renders the compiled `menu` payload the server
 * embeds in widget_config (categories/items/prices/images, all resolved).
 * Until a menu is published into the zone, shows a light placeholder.
 */
const MenuBoardWidget: React.FC<{ cfg: { menu?: MenuBoardPayload } | null }> = ({ cfg }) => {
  const menu = cfg?.menu;
  if (!menu || !(menu.categories && menu.categories.length)) {
    return <PlaceholderWidget label="Menu Board" cfg={cfg as any} />;
  }
  return <MenuBoard payload={menu} />;
};

/**
 * Website / URL widget on the device. Delegates to the shared WebZoneView
 * so preview and device render identically. Interaction on the device is
 * driven by the zone's own allow_interaction / map interaction setting.
 * A failure here is contained to this zone — other zones keep running.
 */
const WebsiteWidget: React.FC<{ cfg: Record<string, any> }> = ({ cfg }) => {
  const wc = readWebsiteConfig(cfg);
  const interactive = wc.allow_interaction;
  return <WebZoneView config={cfg} interactive={interactive} />;
};

/**
 * Keyframes for text animations. Injected once into <head> (idempotent by id)
 * so every surface — editor canvas, preview modal, device player — shares the
 * exact same CSS. Pure CSS/keyframes; no animation library.
 */
const TEXT_ANIM_CSS = `
@keyframes vmFadeIn{from{opacity:0}to{opacity:1}}
@keyframes vmSlideLeft{from{opacity:0;transform:translateX(30%)}to{opacity:1;transform:translateX(0)}}
@keyframes vmSlideRight{from{opacity:0;transform:translateX(-30%)}to{opacity:1;transform:translateX(0)}}
@keyframes vmSlideUp{from{opacity:0;transform:translateY(30%)}to{opacity:1;transform:translateY(0)}}
@keyframes vmSlideDown{from{opacity:0;transform:translateY(-30%)}to{opacity:1;transform:translateY(0)}}
@keyframes vmZoomIn{from{opacity:0;transform:scale(0.7)}to{opacity:1;transform:scale(1)}}
@keyframes vmTypewriter{from{clip-path:inset(0 100% 0 0)}to{clip-path:inset(0 0 0 0)}}
@keyframes vmMarqueeLeft{from{transform:translateX(100%)}to{transform:translateX(-100%)}}
@keyframes vmMarqueeRight{from{transform:translateX(-100%)}to{transform:translateX(100%)}}
`;
let textAnimStylesInjected = false;
function ensureTextAnimStyles(): void {
  if (textAnimStylesInjected || typeof document === "undefined") return;
  if (document.getElementById("vm-text-anim")) { textAnimStylesInjected = true; return; }
  const el = document.createElement("style");
  el.id = "vm-text-anim";
  el.textContent = TEXT_ANIM_CSS;
  document.head.appendChild(el);
  textAnimStylesInjected = true;
}

const ENTRANCE_ANIM: Record<string, string> = {
  fade_in: "vmFadeIn", slide_left: "vmSlideLeft", slide_right: "vmSlideRight",
  slide_up: "vmSlideUp", slide_down: "vmSlideDown", zoom_in: "vmZoomIn",
};

interface TextCfg {
  content?: string; font_size?: number; weight?: number; color?: string;
  align?: "left" | "center" | "right"; line_height?: number; font_family?: string;
  background?: string; animation?: string; animation_duration?: number | string;
  animation_repeat?: "once" | "loop"; marquee_speed?: number | string;
}

const TextWidget: React.FC<{ cfg: TextCfg }> = ({ cfg }) => {
  ensureTextAnimStyles();
  const content = cfg?.content ?? "";
  const anim = cfg?.animation ?? "none";
  const dur = Number(cfg?.animation_duration) > 0 ? Number(cfg.animation_duration) : 1;
  const iter = cfg?.animation_repeat === "loop" ? "infinite" : "1";
  const align = cfg?.align ?? "center";

  // Text styling shared by every animation branch.
  const textStyle: React.CSSProperties = {
    color: cfg?.color || "#fff",
    fontSize: `${cfg?.font_size ?? 48}px`,
    fontWeight: cfg?.weight ?? 700,
    textAlign: align,
    lineHeight: cfg?.line_height ?? 1.1,
    fontFamily: cfg?.font_family || undefined,
  };
  const outer: React.CSSProperties = {
    width: "100%", height: "100%", padding: "0 2%",
    display: "flex", alignItems: "center",
    justifyContent: align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center",
    background: cfg?.background || undefined,
    overflow: "hidden",
  };

  // Marquee: continuous horizontal scroll. Speed = seconds per loop.
  if (anim === "marquee_left" || anim === "marquee_right") {
    const speed = Number(cfg?.marquee_speed) > 0 ? Number(cfg.marquee_speed) : 12;
    const name = anim === "marquee_left" ? "vmMarqueeLeft" : "vmMarqueeRight";
    return (
      <div style={{ ...outer, justifyContent: "flex-start" }}>
        <span style={{
          ...textStyle, whiteSpace: "nowrap", willChange: "transform",
          animation: `${name} ${speed}s linear infinite`,
        }}>
          {content}
        </span>
      </div>
    );
  }

  // Typewriter: stepped left-to-right reveal (clip-path steps).
  if (anim === "typewriter") {
    const steps = Math.max(1, content.length);
    return (
      <div style={outer}>
        <span style={{
          ...textStyle, whiteSpace: "nowrap", display: "inline-block",
          animation: `vmTypewriter ${dur}s steps(${steps}, end) ${iter} both`,
        }}>
          {content}
        </span>
      </div>
    );
  }

  // Entrance animations (fade / slide / zoom), or none.
  const name = ENTRANCE_ANIM[anim];
  return (
    <div style={outer}>
      <span style={{
        ...textStyle,
        ...(name ? { animation: `${name} ${dur}s ease both`, animationIterationCount: iter } : null),
      }}>
        {content}
      </span>
    </div>
  );
};

const ClockWidget: React.FC<{ cfg: { format?: string; timezone?: string; show_date?: boolean; date_format?: string; font_scale?: number } }> = ({ cfg }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    // Tick once per second. Only this widget re-renders — it owns its own
    // state, so the rest of the layout/editor tree is untouched each second.
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const text = formatClock(now, cfg?.format ?? "HH:mm", cfg?.timezone);
  const date = cfg?.show_date ? formatClockDate(now, cfg?.date_format, cfg?.timezone) : null;
  // Scale the auto-fitted base size by the configured percentage (100 = default),
  // clamped to a sane range so a stray value can't blow out the layout.
  const scale = Math.min(300, Math.max(10, Number(cfg?.font_scale ?? 100) || 100)) / 100;
  return (
    <div style={{
      width: "100%", height: "100%",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      color: "#fff", fontFamily: "monospace", fontWeight: 800,
    }}>
      <div style={{ fontSize: `calc(min(40vh, 16vw) * ${scale})`, lineHeight: 1 }}>{text}</div>
      {date && <div style={{ fontSize: `calc(min(9vh, 4vw) * ${scale})`, fontWeight: 600, marginTop: "0.25em", opacity: 0.9 }}>{date}</div>}
    </div>
  );
};

const QrWidget: React.FC<{ cfg: { content?: string; fg?: string; bg?: string } }> = ({ cfg }) => {
  // Use a public QR API generator so the player doesn't need an extra
  // dependency. The server-rendered URL can be swapped in here when a
  // dedicated render service exists.
  const data = encodeURIComponent(cfg?.content ?? "");
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=512x512&data=${data}`;
  return (
    <div style={{
      width: "100%", height: "100%",
      background: cfg?.bg ?? "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "4%",
    }}>
      {cfg?.content
        ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        : <span style={{ color: "#64748b", fontSize: 14, fontWeight: 600 }}>QR URL not configured</span>
      }
    </div>
  );
};

/**
 * Weather zone. Live current weather from the keyless Open-Meteo proxy
 * (`/weather/*`), rendered identically in the designer, Preview and the web
 * player (shared component). Needs a saved latitude/longitude (from the
 * location selector); refreshes every 10 min and caches per location so
 * dragging / re-rendering never refetches. A provider failure degrades to a
 * "Weather unavailable" card — it never breaks the rest of the template.
 */
const WEATHER_BG = "linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)";
const weatherShell: React.CSSProperties = {
  width: "100%", height: "100%",
  display: "flex", flexDirection: "column",
  alignItems: "center", justifyContent: "center",
  gap: 6, color: "#fff", background: WEATHER_BG,
  fontFamily: "Inter, sans-serif", textAlign: "center", padding: 12,
};

const WeatherWidget: React.FC<{ cfg: { latitude?: number; longitude?: number; location_name?: string; location?: string; city?: string; units?: string; font_scale?: number; color?: string } }> = ({ cfg }) => {
  const lat = typeof cfg?.latitude === "number" ? cfg.latitude : null;
  const lon = typeof cfg?.longitude === "number" ? cfg.longitude : null;
  const units: "metric" | "imperial" = cfg?.units === "imperial" ? "imperial" : "metric";
  const name = (cfg?.location_name || cfg?.location || cfg?.city || "").trim();
  // Scale the auto-fitted text by the configured percentage (100 = default),
  // clamped so a stray value can't blow out the layout; colour overrides the
  // default white. Both apply only to the live-data view below.
  const scale = Math.min(300, Math.max(10, Number(cfg?.font_scale ?? 100) || 100)) / 100;
  const color = cfg?.color || "#fff";

  const [data, setData] = useState<CurrentWeather | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (lat === null || lon === null) return;
    let cancelled = false;
    const load = async () => {
      const w = await fetchCurrentWeather(lat, lon, units);
      if (cancelled) return;
      if (w) { setData(w); setFailed(false); } else { setFailed(true); }
    };
    void load();
    const id = window.setInterval(load, WEATHER_TTL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [lat, lon, units]);

  // No coordinates saved yet (or a legacy string-only zone).
  if (lat === null || lon === null) {
    return (
      <div style={weatherShell}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#cbd5e1" }}>Weather location not configured</div>
      </div>
    );
  }
  if (failed && !data) {
    return (
      <div style={weatherShell}>
        <div style={{ fontSize: 22, opacity: 0.8 }}>⚠️</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#cbd5e1" }}>Weather unavailable</div>
        {name && <div style={{ fontSize: 11, opacity: 0.6 }}>{name}</div>}
      </div>
    );
  }
  if (!data) {
    return <div style={weatherShell}><div style={{ fontSize: 12, opacity: 0.6 }}>Loading weather…</div></div>;
  }

  return (
    <div style={{ ...weatherShell, color }}>
      <div style={{ fontSize: `calc(min(22vh, 9vw) * ${scale})`, lineHeight: 1 }}>{weatherEmoji(data.weather_code, data.is_day)}</div>
      <div style={{ fontSize: `calc(min(16vh, 7vw) * ${scale})`, fontWeight: 300, lineHeight: 1 }}>{data.temperature}{data.unit}</div>
      <div style={{ fontSize: `calc(min(5vh, 2.4vw) * ${scale})`, fontWeight: 700, opacity: 0.95 }}>{data.condition}</div>
      {name && <div style={{ fontSize: `calc(min(4vh, 2vw) * ${scale})`, fontWeight: 500, opacity: 0.7 }}>{name}</div>}
    </div>
  );
};

const PlaceholderWidget: React.FC<{ label: string; cfg: Record<string, unknown> | null | undefined }> = ({ label, cfg }) => (
  <div style={{
    width: "100%", height: "100%",
    background: "rgba(255,255,255,0.04)",
    border: "1px dashed rgba(255,255,255,0.18)",
    color: "#fff",
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    fontFamily: "Inter, sans-serif",
    textAlign: "center",
    padding: 12,
  }}>
    <div style={{
      fontSize: 12, fontWeight: 800,
      textTransform: "uppercase", letterSpacing: "0.08em",
      opacity: 0.85,
    }}>{label}</div>
    {/* A `note` is a human message (e.g. "Enter a video URL") — shown plainly.
        Otherwise fall back to a compact config dump for other placeholders. */}
    {cfg && typeof cfg.note === "string" ? (
      <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6, maxWidth: "100%" }}>{cfg.note}</div>
    ) : cfg && Object.keys(cfg).length > 0 ? (
      <div style={{ fontSize: 10, opacity: 0.55, marginTop: 6, maxWidth: "100%", overflow: "hidden" }}>
        {Object.entries(cfg).slice(0, 3).map(([k, v]) =>
          `${k}: ${String(v ?? "")}`).join(" · ")}
      </div>
    ) : null}
  </div>
);

/**
 * Time-of-day parts in a specific IANA timezone, via Intl (so DST is correct).
 * Falls back to local time when the timezone is invalid/unset. No date-fns/luxon
 * dependency — Intl is built in and identical on the admin and the player.
 */
function tzParts(d: Date, tz: string | undefined): { hh24: number; mm: number; ss: number } {
  if (tz) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz, hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit",
      }).formatToParts(d);
      const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0");
      let hh24 = get("hour");
      if (hh24 === 24) hh24 = 0; // Intl can emit "24" at midnight
      return { hh24, mm: get("minute"), ss: get("second") };
    } catch {
      /* invalid tz → fall through to local */
    }
  }
  return { hh24: d.getHours(), mm: d.getMinutes(), ss: d.getSeconds() };
}

function formatClock(d: Date, format: string, tz: string | undefined): string {
  // Minimal format engine: HH (24), hh (12), mm, ss, a (am/pm). Timezone-aware
  // via Intl so DST is handled correctly for IANA zones (e.g. America/Toronto).
  const pad = (n: number) => String(n).padStart(2, "0");
  const { hh24, mm, ss } = tzParts(d, tz);
  const hh12 = ((hh24 + 11) % 12) + 1;
  const ampm = hh24 < 12 ? "AM" : "PM";
  return format
    .replace(/HH/g, pad(hh24))
    .replace(/hh/g, pad(hh12))
    .replace(/mm/g, pad(mm))
    .replace(/ss/g, pad(ss))
    .replace(/a/g, ampm);
}

/** Localised date string in the target timezone (e.g. "Tue, Aug 25"). */
function formatClockDate(d: Date, dateFormat: string | undefined, tz: string | undefined): string {
  const opts: Intl.DateTimeFormatOptions =
    dateFormat === "long" ? { weekday: undefined, year: "numeric", month: "long", day: "numeric" } :
    dateFormat === "iso"  ? { year: "numeric", month: "2-digit", day: "2-digit" } :
                            { weekday: "short", month: "short", day: "numeric" }; // default "short"
  if (tz) opts.timeZone = tz;
  try {
    if (dateFormat === "iso") {
      // en-CA yields YYYY-MM-DD.
      return new Intl.DateTimeFormat("en-CA", opts).format(d);
    }
    return new Intl.DateTimeFormat(undefined, opts).format(d);
  } catch {
    return new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" }).format(d);
  }
}

export default LayoutRenderer;
